import { blueGreyMui } from "@ryde/theme";

import {
  DragTypeEnum,
  EditFormMode,
  RideRoutes,
  RidesKanbanSortEnum,
  RideStatusEnum,
} from "../enums";
import type {
  RideEntity,
  RydeUiUnit,
  TRideCrudModeKey,
  TRideDtoStartEndKey,
  TRideMetadataFields,
} from "../models";
import { ComputedDataKey } from "./common.const";

export const RIDE_ALWAYS_DIRTY_FIELDS: Partial<
  Record<keyof RideEntity, boolean>
> = { rideStart: true, rideEnd: true, rideTypeId: true };

export const NEW_RIDE_ADDITIONAL_ALWAYS_DIRTY_FIELDS: Partial<
  Record<keyof RideEntity, boolean>
> = { direction: true };

export const RIDE_DROP_MAX_TIME_DIFFERENCE_MINUTES = 720;

const INVALID_RIDE_DRAG_STATUS_VALUES = [
  RideStatusEnum.Done,
  RideStatusEnum.Canceled,
  RideStatusEnum.DoneApproved,
] as const;

export type TInvalidRideDragStatus =
  (typeof INVALID_RIDE_DRAG_STATUS_VALUES)[number];

export const INVALID_RIDE_DRAG_STATUSES: readonly RideStatusEnum[] =
  INVALID_RIDE_DRAG_STATUS_VALUES;

const RIDE_DRAG_TYPE_VALUES = [
  DragTypeEnum.KANBAN_RIDE,
  DragTypeEnum.GRID_RIDE,
  DragTypeEnum.CARD_BOARD_RIDE,
] as const;

export type TRideDragType = (typeof RIDE_DRAG_TYPE_VALUES)[number];

export const RIDE_DRAG_TYPES: readonly DragTypeEnum[] = RIDE_DRAG_TYPE_VALUES;

/** Every ride edit route base, for pathname checks (see `isRidesEditRoutePathname`). */
export const RIDE_EDIT_ROUTE_BASES: string[] = [
  RideRoutes.EDIT_BASE_MAP,
  RideRoutes.EDIT_BASE_CARD_BOARD,
  RideRoutes.EDIT_BASE_GRID_MULTI_SELECT,
  RideRoutes.EDIT_BASE_GRID_SINGLE_SELECT,
  RideRoutes.EDIT_BASE_KANBAN,
  RideRoutes.EDIT_BASE_CALENDAR,
];

/** Every ride add route base, for pathname checks (see `isRidesAddRoutePathname`). */
export const RIDE_ADD_ROUTE_BASES: string[] = [
  RideRoutes.ADD_MAP,
  RideRoutes.ADD_GRID_MULTI_SELECT,
  RideRoutes.ADD_CARD_BOARD,
  RideRoutes.ADD_GRID_SINGLE_SELECT,
  RideRoutes.ADD_KANBAN,
  RideRoutes.ADD_CALENDAR,
];

export const RideStartEndOppositeKey: Record<
  TRideDtoStartEndKey,
  TRideDtoStartEndKey
> = {
  rideStart: "rideEnd",
  rideEnd: "rideStart",
};

export const RideCrudModeKey: TRideCrudModeKey = {
  uiUnit: "ride",
  formMode: EditFormMode.CrudRoute,
};

/* should be hard-coded, because depends on hard-coded values in use-ride-dto-actions
   also in ride live map page the page context is "rideMap", but for editing crud needs "ride" */
export const RideCrudModeKeyTuple: [RydeUiUnit, EditFormMode] = [
  RideCrudModeKey.uiUnit,
  RideCrudModeKey.formMode,
];

/** Keep in sync with `@ryde/shared` ride.constants on the backend. */
export enum RideComputedDataKeys {
  courseLineColor = "courseLineColor",
  newIncidentsCount = "newIncidentsCount",
  liveMapStats = "liveMapStats",
  driverApproved = "driverApproved",
}

export const rideComputedDataCourseLineColorGridPath =
  `${ComputedDataKey}.${RideComputedDataKeys.courseLineColor}` as const;

export const rideComputedDataNewIncidentsCountGridPath =
  `${ComputedDataKey}.${RideComputedDataKeys.newIncidentsCount}` as const;

export const rideComputedDataLiveMapStatsGridPath =
  `${ComputedDataKey}.${RideComputedDataKeys.liveMapStats}` as const;

export const rideComputedDataDriverApprovedGridPath =
  `${ComputedDataKey}.${RideComputedDataKeys.driverApproved}` as const;

// keep in sync with BE same constant
export const RIDE_METADATA_EDITABLE_FIELDS: TRideMetadataFields[] = [
  "tags",
  "remarks",
  "keepSupplier",
  "dock",
  "manualPrice",
  "supplierManualPrice",
  "approver",
  "postReferentId",
  "postReceiver",
  "postReceiverPhone",
  "title",
  "invoice",
  "flightNumber",
  "duplicationsNumber",
];

export const RIDE_POST_REFERENT_FIELD_NAME =
  "postReferentId" satisfies TRideMetadataFields;

/** Max rows selectable at once in rides grid and live map (manual selection). Keep in sync with BE `ride.constants`. */
export const RIDE_TABLE_MAX_SELECTED_ROWS = 50;

export const RIDES_KANBAN_DEFAULT_SORT = RidesKanbanSortEnum.UpdatedDesc;

export const RIDE_AREA_TITLES_DELIMITER = ", ";

export const MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD = 2;
export const MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION = 2;
/** PickUp/DropOff segment duration needs at least one interior passenger stop (3 stations). */
export const MINIMAL_STATIONS_COUNT_FOR_BOARDING_SEGMENT_DURATION = 3;
/** Legacy PickUp boarding segment (no passengers): `sumDurationLegs` exclusive end index = stationCount - offset (excludes final leg). */
export const BOARDING_SEGMENT_LEGACY_PICKUP_EXCLUSIVE_END_OFFSET = 2;
export const DIRECT_RIDE_STATIONS_COUNT = 2;

export const RIDE_START_END_NON_RE_COUNTABLE_TIME_GAP_MINUTES = 120;
// Keep in sync with backend: 2 minutes so 1-minute ceil rounding cannot collapse two stations.
export const INDEPENDENT_STATION_BOUND_GAP_MINUTES = 2;

export const DEFAULT_DRAG_GRIP_COLOR = blueGreyMui._200;

/** Shift-time UI checks downgraded to info log when allowShiftTimeOverrideDuringManualAction is enabled. */
export const RideShiftTimeChecksSkippedByAllowOverride = [
  "directionADateChangedRideArriveAfterShiftStart",
  "directionBDateChangedRideDepartBeforeShiftEnd",
] as const;

/** Backend soft-validation / timing checks covered by the same override (logged server-side). */
export const RideShiftTimeBackendChecksSkippedByAllowOverride = [
  "ShiftTimeAlignment",
  "GeoProdMaxShiftGap",
  "RideAndPassengerDatesWithinAcceptableRange",
  "BaseStationWaitingTimeExceedsShiftTime",
  "TimeStationsPickupArrivalTime",
  "TimeStationsDropOffArrivalTime",
] as const;

export type TRideShiftTimeCheckSkippedByAllowOverride =
  (typeof RideShiftTimeChecksSkippedByAllowOverride)[number];

export type TRideShiftTimeBackendCheckSkippedByAllowOverride =
  (typeof RideShiftTimeBackendChecksSkippedByAllowOverride)[number];
